/**
 * La campana. Pequeña a propósito — casi todo lo que necesita ya está en caché.
 *
 * Dos detalles que no son obvios:
 *
 * - El conteo está limitado a «99+». No es cosmético: un número sin límite
 *   hace el badge más ancho que el botón y mueve el layout del encabezado, y la
 *   diferencia entre 143 y 217 avisos no leídos no es información sobre la que
 *   alguien actúe.
 * - El `aria-label` lleva el conteo, porque un punto de color sobre un ícono es
 *   invisible para un lector de pantalla. El número visible es `aria-hidden`
 *   para no leerse dos veces.
 */
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { cn } from '@/shared/lib/cn';

import { useNotificationSocketStatus } from '../notificationSocketContext';
import { useUnreadCount } from '../useNotifications';

const MAX_DISPLAYED = 99;

export function NotificationBell() {
  const { t } = useTranslation('notifications');
  const { data: unread = 0 } = useUnreadCount();
  const status = useNotificationSocketStatus();

  const label = unread > MAX_DISPLAYED ? `${MAX_DISPLAYED}+` : String(unread);

  return (
    <Link
      to="/notificaciones"
      className="relative inline-flex h-9 w-9 items-center justify-center rounded-full"
      aria-label={t('bell.label', { count: unread })}
    >
      <Bell className="text-ink-muted size-5" aria-hidden />

      {unread > 0 && (
        <span
          aria-hidden
          className={cn(
            'absolute -right-0.5 -top-0.5 inline-flex min-w-5 items-center justify-center',
            'rounded-full bg-danger px-1 text-xs font-medium text-white',
          )}
        >
          {label}
        </span>
      )}

      {/*
        Un indicador de desconexión, y solo mientras está genuinamente
        desconectado — nunca durante la primera conexión, que parpadearía en
        cada carga de página. Está ahí porque el modo de fallo honesto de esta
        feature es el silencio, y un usuario que puede ver que el canal en vivo
        está caído sabe recargar en vez de confiar en una campana vieja.
      */}
      {status === 'reconnecting' && (
        <span
          className="absolute -bottom-0.5 -right-0.5 size-2 rounded-full bg-warning"
          title={t('bell.reconnecting')}
        />
      )}
    </Link>
  );
}
